/**
 * AppSettingsContext — Persistent application settings
 * Stores: appName, logoDataUrl, refreshInterval, theme, notification preferences
 * Persisted in localStorage so settings survive page reloads.
 */

import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';

// ============ Types ============

export type AppTheme = 'light' | 'dark' | 'system';
export type AlertThreshold = 'all' | 'warning' | 'error';

export interface AppSettings {
  appName: string;
  logoDataUrl: string | null; // base64 data-URL or null (default SVG logo)
  refreshInterval: number;    // seconds
  theme: AppTheme;
  // Notification preferences
  browserNotifications: boolean;
  soundAlerts: boolean;
  alertThreshold: AlertThreshold;
}

interface AppSettingsContextValue extends AppSettings {
  updateSettings: (patch: Partial<AppSettings>) => void;
  /** Resolved theme after applying "system" preference */
  resolvedTheme: 'light' | 'dark';
}

// ============ Defaults ============

const STORAGE_KEY = 'r4s-app-settings';

const DEFAULT_SETTINGS: AppSettings = {
  appName: 'R4S OmniMens',
  logoDataUrl: null,
  refreshInterval: 60,
  theme: 'light',
  browserNotifications: false,
  soundAlerts: false,
  alertThreshold: 'all',
};

function loadSettings(): AppSettings {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULT_SETTINGS };
    const parsed = JSON.parse(raw);
    return {
      appName: typeof parsed.appName === 'string' ? parsed.appName : DEFAULT_SETTINGS.appName,
      logoDataUrl: typeof parsed.logoDataUrl === 'string' ? parsed.logoDataUrl : null,
      refreshInterval:
        typeof parsed.refreshInterval === 'number' && parsed.refreshInterval >= 10
          ? parsed.refreshInterval
          : DEFAULT_SETTINGS.refreshInterval,
      theme: ['light', 'dark', 'system'].includes(parsed.theme) ? parsed.theme : DEFAULT_SETTINGS.theme,
      browserNotifications: typeof parsed.browserNotifications === 'boolean' ? parsed.browserNotifications : DEFAULT_SETTINGS.browserNotifications,
      soundAlerts: typeof parsed.soundAlerts === 'boolean' ? parsed.soundAlerts : DEFAULT_SETTINGS.soundAlerts,
      alertThreshold: ['all', 'warning', 'error'].includes(parsed.alertThreshold) ? parsed.alertThreshold : DEFAULT_SETTINGS.alertThreshold,
    };
  } catch {
    return { ...DEFAULT_SETTINGS };
  }
}

function saveSettings(s: AppSettings) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(s));
  } catch {
    // quota exceeded — silently ignore
  }
}

// ============ Context ============

const AppSettingsContext = createContext<AppSettingsContextValue | null>(null);

export function AppSettingsProvider({ children }: { children: ReactNode }) {
  const [settings, setSettings] = useState<AppSettings>(loadSettings);

  // Resolve "system" theme
  const [systemPrefersDark, setSystemPrefersDark] = useState(
    () => window.matchMedia('(prefers-color-scheme: dark)').matches,
  );

  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent) => setSystemPrefersDark(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const resolvedTheme: 'light' | 'dark' =
    settings.theme === 'system'
      ? (systemPrefersDark ? 'dark' : 'light')
      : settings.theme;

  // Apply theme class to <html>
  useEffect(() => {
    const root = document.documentElement;
    if (resolvedTheme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
  }, [resolvedTheme]);

  // Persist on change
  useEffect(() => {
    saveSettings(settings);
  }, [settings]);

  const updateSettings = useCallback((patch: Partial<AppSettings>) => {
    setSettings((prev) => ({ ...prev, ...patch }));
  }, []);

  return (
    <AppSettingsContext.Provider value={{ ...settings, updateSettings, resolvedTheme }}>
      {children}
    </AppSettingsContext.Provider>
  );
}

export function useAppSettings(): AppSettingsContextValue {
  const ctx = useContext(AppSettingsContext);
  if (!ctx) throw new Error('useAppSettings must be used within AppSettingsProvider');
  return ctx;
}
